/**
 * Health notes and the waiver at join (Pilates studio shape, C2, migration 0077).
 *
 * The form is off for every business until the owner turns it on in Desk (Settings, Health). When it
 * is on, a new person answers short yes or no questions when they join, and accepts the business
 * terms (the waiver). A yes asks for a short note. For a family business the parent answers for each
 * child: the form is about the person who takes the class.
 *
 * The team sees a flag ("Health note"), not the details. The owner and the admins read the details;
 * staff read them only when the owner allows it ("Instructors can read health notes"). The server
 * decides, never the screen.
 *
 * Answers are health data: they never go in an export, an analytics event, the AI Assistant context
 * or a log. Plain values here, with no I/O, so the API, Desk and the phone share them.
 */

/** One question of the form. `id` stays the same when the owner changes the words. */
export type HealthQuestion = { id: string; text: string };

/** One answer: the question as the person read it, yes or no, and the note of a yes. */
export type HealthAnswer = { questionId: string; question: string; yes: boolean; note: string | null };

export const HEALTH_QUESTIONS_MAX = 10;
export const HEALTH_QUESTION_TEXT_MAX = 160;
export const HEALTH_NOTE_MAX = 300;
export const WAIVER_TEXT_MAX = 4000;

/**
 * The questions Teamna offers when the owner turns the form on. The words are about the person who
 * takes the class, so a parent can answer them for a child too.
 */
export const defaultHealthQuestions: readonly HealthQuestion[] = [
  { id: "injury", text: "Is there an injury or pain now?" },
  { id: "pregnancy", text: "Is there a pregnancy now, or a birth in the last 6 months?" },
  { id: "heart", text: "Is there a heart condition or a blood pressure condition?" },
  { id: "surgery", text: "Was there surgery in the last 12 months?" },
];

/** The terms Teamna offers as a start. The owner reads them and changes them before they save. */
export const defaultWaiverText =
  "I take part in the sessions at my own risk. " +
  "Before a session, I tell the team about an injury, a pain or a change in my health. " +
  "I obey the instructions of the team. " +
  "I stop when I feel pain or feel unwell, and I tell the team.";

/** A yes on any question: the team sees the "Health note" flag. */
export function healthHasFlag(answers: ReadonlyArray<Pick<HealthAnswer, "yes">>): boolean {
  return answers.some((answer) => answer.yes);
}

/** A new question id: short, unique in the list, never a word a person typed. */
export function newHealthQuestionId(existing: ReadonlyArray<Pick<HealthQuestion, "id">>): string {
  const taken = new Set(existing.map((question) => question.id));
  for (let n = existing.length + 1; ; n += 1) {
    const id = `q${n}`;
    if (!taken.has(id)) return id;
  }
}

/** The words of the flag, the same on every screen. */
export const HEALTH_FLAG_LABEL = "Health note";

/** What a person chose on the step so far: yes, no or nothing yet, and the note of a yes. */
export type HealthDraft = Record<string, { yes: boolean | null; note: string }>;

/** Every question has a yes or a no, and the terms (when there are terms) are accepted. */
export function healthStepReady(
  questions: ReadonlyArray<HealthQuestion>,
  draft: HealthDraft,
  hasWaiver: boolean,
  accepted: boolean,
): boolean {
  return questions.every((question) => typeof draft[question.id]?.yes === "boolean") && (!hasWaiver || accepted);
}

/** The answers of the request: a note goes with a yes only. */
export function healthAnswersBody(
  questions: ReadonlyArray<HealthQuestion>,
  draft: HealthDraft,
): Array<{ questionId: string; yes: boolean; note: string | null }> {
  return questions.map((question) => {
    const entry = draft[question.id];
    const yes = entry?.yes === true;
    const note = yes ? entry?.note.trim() || null : null;
    return { questionId: question.id, yes, note };
  });
}

/**
 * The words of the step, the same on Desk and the phone. A parent answers for a child (`forChildren`);
 * a client answers for themselves. `staffCanRead` is the owner's switch, so the step tells the truth
 * about who reads the answers.
 */
export function healthStepWords(input: {
  forChildren: boolean;
  personName: string;
  businessName: string;
  staffCanRead: boolean;
}): { title: string; intro: string; notePrompt: string; waiverTitle: string; acceptLabel: string; submit: string } {
  const who = input.staffCanRead
    ? `The owner and the team of ${input.businessName} read the answers.`
    : `The owner and the admins of ${input.businessName} read the answers. The team sees only that there is a health note.`;
  return {
    title: input.forChildren ? `Health questions for ${input.personName}` : "Health questions",
    intro: input.forChildren
      ? `Answer for ${input.personName}, yes or no. The team can then take care in each session. ${who}`
      : `Answer yes or no. The team can then take care in each session. ${who}`,
    notePrompt: "Write a short note (optional)",
    waiverTitle: "Terms of the business",
    acceptLabel: input.forChildren ? `I accept these terms for ${input.personName}` : "I accept these terms",
    submit: "Save and continue",
  };
}

/** Only a path inside the app, never another site: the `next` of the step, or `fallback`. */
export function healthStepNext(next: string | undefined, fallback: string): string {
  return next && next.startsWith("/") && !next.startsWith("//") ? next : fallback;
}

/**
 * The address of the step, with where to go after it. Desk opens `/member/health/<id>`, the phone
 * opens `/health/<id>`: `base` says which.
 */
export function healthStepPath(base: "/member/health" | "/health", businessId: string, next: string): string {
  return `${base}/${businessId}?next=${encodeURIComponent(next)}`;
}
